import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import intl from 'react-intl-universal';
import api from 'utils/api';
import { Button, Stack, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import FormField from 'components/FormField';
import { QuotaProptypes } from 'shared/propTypes/quotaType';
import { DOLPHIN_API } from 'shared/constants/apis';
import { ConfirmClientSettingsModal } from './confirmClientSettingsModal';
import pxToRem from 'assets/theme/functions/pxToRem';
import { useSnackbar } from 'notistack';
import logger from 'utils/logger';

const DEFAULT_VALUES = {
  QUOTA: 100,
  MONTHLY_INVOICE_DATE: 25,
};

const propTypes = {
  quotaSrcOrg: PropTypes.shape(QuotaProptypes),
};

// Create a logger instance for this component
const log = logger.createLogger('ClientSettings');

const ClientSettings = ({ quotaSrcOrg }) => {
  const snackbar = useSnackbar();
  const [showConfirmBeforeSend, setShowConfirmBeforeSend] = useState(false);
  const [formState, setFormState] = useState({});
  const [monthlyInvoiceDateValue, setMonthlyInvoiceDateValue] = useState(1);
  const [initialState, setInitialState] = useState({});

  useEffect(() => {
    const newState = {
      contractId: quotaSrcOrg.id,
      srcOrgId: quotaSrcOrg.srcOrgId,
      dstOrgId: quotaSrcOrg.dstOrgId,
      dstOrgName: quotaSrcOrg.dstOrg.name,
      quota:
        quotaSrcOrg.quota != null
          ? parseInt(quotaSrcOrg.quota) === 0
            ? DEFAULT_VALUES.QUOTA
            : parseInt(quotaSrcOrg.quota)
          : DEFAULT_VALUES.QUOTA,
      monthlyInvoiceDate:
        parseInt(quotaSrcOrg.monthlyInvoiceDate) ||
        DEFAULT_VALUES.MONTHLY_INVOICE_DATE,
    };
    setFormState(newState);
    setInitialState(newState);
    setMonthlyInvoiceDateValue(quotaSrcOrg.monthlyInvoiceDate);
  }, [quotaSrcOrg]);

  const hasChanges = () => {
    return (
      formState.quota !== initialState.quota ||
      formState.monthlyInvoiceDate !== initialState.monthlyInvoiceDate
    );
  };

  //! Only until 28th since the month might not have 31 days
  const billingDateOptions = Array.from({ length: 28 }, (_, i) => ({
    label: (i + 1).toString(),
    value: (i + 1).toString(),
  }));

  const handleChangeBillingDate = (newValue) => {
    const newFormState = {
      ...formState,
      monthlyInvoiceDate: parseInt(newValue),
    };
    setFormState({ ...newFormState });
  };

  const handleClick = async (update, sendServer) => {
    if (update && !hasChanges()) {
      snackbar.enqueueSnackbar(
        intl.get('organization_settings_client_management_no_changes'),
        {
          variant: 'warning',
        }
      );
      return;
    }
    setShowConfirmBeforeSend(update);
    if (!update && sendServer) {
      await sendToServer();
    }
  };

  const sendToServer = async () => {
    const variables = {
      params: {
        organizationId: quotaSrcOrg.srcOrgId,
      },
      data: { ...formState },
    };

    api.post(DOLPHIN_API.CLIENT_MANAGEMENT, variables).then(
      () => {
        snackbar.enqueueSnackbar(intl.get('shared_data_was_updated'), {
          variant: 'success',
        });
        window.location.reload();
      },
      (error) => {
        log.error('Error in client management API', { error });
      }
    );
  };

  return (
    <>
      <Stack spacing={3}>
        <Typography variant="h5" gutterBottom>
          {intl.get(
            'organization_settings_client_management_text_billing_date'
          )}
        </Typography>
        <Autocomplete
          id="billingDate"
          value={monthlyInvoiceDateValue.toString()}
          onChange={(_, newValue) => {
            if (newValue) {
              handleChangeBillingDate(newValue.value);
              setMonthlyInvoiceDateValue(parseInt(newValue.value));
            }
          }}
          isOptionEqualToValue={(option, value) => {
            return option.label === value;
          }}
          options={billingDateOptions}
          renderInput={(params) => (
            <FormField
              {...params}
              variant="outlined"
              label={intl.get(
                'organization_settings_client_management_label_billing_date'
              )}
              InputLabelProps={{ shrink: true }}
            />
          )}
        />
        <Button
          variant="contained"
          color="primary"
          style={{
            color: 'white',
            fontSize: pxToRem(18),
          }}
          fullWidth
          onClick={() => handleClick(true)}>
          {intl.get('organization_settings_client_management_button_update')}
        </Button>
      </Stack>

      <ConfirmClientSettingsModal
        quotaSrcOrg={quotaSrcOrg}
        formState={formState}
        showConfirmBeforeSend={showConfirmBeforeSend}
        handleClick={handleClick}
      />
    </>
  );
};

ClientSettings.propTypes = propTypes;

export default ClientSettings;
